/*
 * Tiered discount progress bar (snippets/cart-tier-bar.liquid).
 *
 * Every bar carries its tiers as [minimum in shop currency (cents), discount in percent],
 * the cart subtotal in the visitor's currency (cents) and the texts. This script converts
 * the minimums at Shopify's own rate (Shopify.currency.rate) when the visitor pays in
 * another currency (PLN, SEK, DKK), works out the next step and draws text, fill and step
 * markers. A tier from 0 applies to every cart.
 *
 * The cart drawer and the cart page are replaced wholesale after every cart change
 * (Section Rendering API), so a MutationObserver redraws any bar that appears. The rules
 * are pure functions on window.GbTierBar for tests/unit/cart-tier-bar.test.js.
 *
 * With the Black Friday campaign switched on (window.GB_CAMPAIGN), the campaign decides
 * when the bar may show: during the sale, and before it only for visitors with early
 * access (assets/campaign-bar.js). That script loads after this one and calls
 * GbTierBar.redraw() once it knows, so until then the bar stays hidden.
 */
(function (root) {
  "use strict";

  /* Tier minimums in the visitor's currency, rounded to the cent like Shopify's conversion. */
  function convertTiers(tiers, rate) {
    var factor = Number(rate) > 0 ? Number(rate) : 1;

    return tiers
      .map(function (tier) {
        return { min: Math.round(Number(tier[0]) * factor), discount: Number(tier[1]) };
      })
      .filter(function (tier) {
        return tier.min >= 0 && tier.discount > 0;
      })
      .sort(function (a, b) {
        return a.min - b.min;
      });
  }

  /*
   * Where the cart stands: the tier it reached, the next one, what is left to it, and
   * how far the bar is filled (0..1 of the highest tier).
   */
  function tierState(subtotal, tiers) {
    var reached = null;
    var next = null;

    for (var i = 0; i < tiers.length; i++) {
      if (subtotal >= tiers[i].min) reached = tiers[i];
      else if (!next) next = tiers[i];
    }

    var top = tiers.length ? tiers[tiers.length - 1].min : 0;

    return {
      reached: reached,
      next: next,
      remaining: next ? next.min - subtotal : 0,
      progress: top > 0 ? Math.max(0, Math.min(1, subtotal / top)) : 0,
      steps: tiers.map(function (tier) {
        return {
          at: top > 0 ? tier.min / top : 0,
          done: subtotal >= tier.min,
          discount: tier.discount,
        };
      }),
    };
  }

  /* Whole amounts without decimals (€5), others with two (€12,50), in the page's locale. */
  function formatAmount(cents, locale, before, after) {
    var whole = cents % 100 === 0;
    var number;

    try {
      number = new Intl.NumberFormat(locale || "nl", {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(cents / 100);
    } catch {
      number = whole ? String(cents / 100) : (cents / 100).toFixed(2);
    }

    return (before || "") + number + (after || "");
  }

  /* "10%", or "10 %" where the locale writes it so. */
  function formatPercent(value, locale) {
    try {
      return new Intl.NumberFormat(locale || "nl", {
        style: "percent",
        maximumFractionDigits: 1,
      }).format(value / 100);
    } catch {
      return value + "%";
    }
  }

  function fill(template, values) {
    return String(template || "")
      .replace("%REMAINING%", values.remaining || "")
      .replace("%DISCOUNT%", values.discount || "");
  }

  /* The sentence for a state, or '' when there is nothing to say. */
  function tierText(state, texts, format, percent) {
    if (state.next) {
      return fill(texts.progress, {
        remaining: format(state.remaining),
        discount: percent(state.next.discount),
      });
    }

    if (state.reached) return fill(texts.max, { discount: percent(state.reached.discount) });

    return "";
  }

  /*
   * Factor from shop currency to the visitor's currency: 1 when they are the same,
   * Shopify's own rate otherwise, null when that rate is missing.
   */
  function currencyRate(currency, shopCurrency, shopifyRate) {
    if (!currency || currency === shopCurrency) return 1;
    var rate = Number(shopifyRate);

    return rate > 0 ? rate : null;
  }

  var api = {
    currencyRate: currencyRate,
    convertTiers: convertTiers,
    tierState: tierState,
    formatAmount: formatAmount,
    formatPercent: formatPercent,
    tierText: tierText,
  };

  root.GbTierBar = api;

  if (typeof document === "undefined") return;

  function rateFor(bar) {
    return currencyRate(
      bar.getAttribute("data-currency"),
      bar.getAttribute("data-shop-currency"),
      root.Shopify && root.Shopify.currency && root.Shopify.currency.rate,
    );
  }

  /* true, false, or null while the campaign script has not decided yet. */
  function campaignAllows() {
    if (!root.GB_CAMPAIGN) return true;
    var campaign = root.GbCampaignBar;

    return campaign && campaign.showsTierBar instanceof Function ? campaign.showsTierBar() : null;
  }

  function render(bar) {
    var allowed = campaignAllows();

    if (allowed === null) return;

    if (!allowed) {
      bar.hidden = true;
      bar.setAttribute("data-tier-rendered", "");

      return;
    }

    var rate = rateFor(bar);
    var tiers;

    try {
      tiers = JSON.parse(bar.getAttribute("data-tiers") || "[]");
    } catch {
      tiers = [];
    }

    /* Without a rate for a foreign currency the amounts would be euros labelled as kronor: hide. */
    if (rate === null || !tiers.length) {
      bar.hidden = true;

      return;
    }

    var converted = convertTiers(tiers, rate);
    var state = tierState(Number(bar.getAttribute("data-subtotal")) || 0, converted);
    var locale = bar.getAttribute("data-locale");
    var before = bar.getAttribute("data-before");
    var after = bar.getAttribute("data-after");

    var text = tierText(
      state,
      { progress: bar.getAttribute("data-text-progress"), max: bar.getAttribute("data-text-max") },
      function (cents) {
        return formatAmount(cents, locale, before, after);
      },
      function (value) {
        return formatPercent(value, locale);
      },
    );

    var textEl = bar.querySelector("[data-tier-text]");
    var fillEl = bar.querySelector("[data-tier-fill]");
    var stepsEl = bar.querySelector("[data-tier-steps]");

    if (textEl && textEl.textContent !== text) textEl.textContent = text;

    if (fillEl) fillEl.style.width = (state.progress * 100).toFixed(2) + "%";

    if (stepsEl && !stepsEl.childElementCount) {
      state.steps.forEach(function (step) {
        var mark = document.createElement("span");
        mark.className = "cart-tier-bar__step";
        mark.style.left = (step.at * 100).toFixed(2) + "%";
        /* The discount each step gives, under its marker, so the steps say what they are worth. */
        var label = document.createElement("span");
        label.className = "cart-tier-bar__label";
        label.textContent = formatPercent(step.discount, locale);
        mark.appendChild(label);
        stepsEl.appendChild(mark);
      });
    }

    if (stepsEl) {
      Array.prototype.forEach.call(stepsEl.children, function (mark, index) {
        mark.classList.toggle("is-done", Boolean(state.steps[index] && state.steps[index].done));
      });
    }

    bar.classList.toggle("is-complete", !state.next && Boolean(state.reached));
    bar.hidden = false;
    bar.setAttribute("data-tier-rendered", "");
  }

  function renderAll() {
    var bars = document.querySelectorAll("[data-tier-bar]:not([data-tier-rendered])");

    for (var i = 0; i < bars.length; i++) render(bars[i]);
  }

  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    root.requestAnimationFrame(function () {
      scheduled = false;
      renderAll();
    });
  }

  /* Draw every bar again, for when the campaign phase changes. */
  api.redraw = function () {
    var bars = document.querySelectorAll("[data-tier-bar]");

    for (var i = 0; i < bars.length; i++) bars[i].removeAttribute("data-tier-rendered");
    renderAll();
  };

  renderAll();

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  }
})(typeof window !== "undefined" ? window : globalThis);
